import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import './ProductImages.scss'
import axios from "axios";
import { ensureMediaUrl } from "../../utils/mediaUrl";

/** Match mobile PDP: relative paths like `assets/prod1.webp` must use API origin, not `/` (CRA host). Absolute Cloudinary URLs pass through. */
function toAbsoluteProductImageUrl(url) {
    return ensureMediaUrl(url) || null;
}

export function ProductImages({ product_id }) {

    const [images, setImages] = useState([]);
    const [mainImage, setMainImage] = useState(images?.[0] || null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);

    useEffect(() => {
        getImages(product_id)
    }, [product_id])

    useEffect(() => {
        if (images.length) setMainImage(images[0]);
    }, [images.length])

    async function getImages(params) {
        try {
            const token = localStorage.getItem("token");
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            const response = await axios.post(
                `/products/specifications/${params}`,
                {},
                config
            );
            const data = response.data;
            if (data?.["urls"]?.length) {
                const prefixed_urls = data.urls
                    .map((url) => toAbsoluteProductImageUrl(url))
                    .filter(Boolean);
                setImages(prefixed_urls);
            } else {
                setImages([]);
            }
        } catch (error) {
            console.error("Failed to fetch product images:", error);
            setImages([]);
        }
    }

    function changeImage(arg) {
        setMainImage(images[arg]);
    }

    const openModal = useCallback((index) => {
        if (images.length && index >= 0 && index < images.length) {
            setModalIndex(index);
            setModalOpen(true);
        }
    }, [images.length]);

    const closeModal = useCallback(() => setModalOpen(false), []);

    const goPrev = useCallback(() => {
        setModalIndex((i) => (i <= 0 ? images.length - 1 : i - 1));
    }, [images.length]);

    const goNext = useCallback(() => {
        setModalIndex((i) => (i >= images.length - 1 ? 0 : i + 1));
    }, [images.length]);

    useEffect(() => {
        if (!modalOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeModal();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [modalOpen, closeModal, goPrev, goNext]);

    const currentImage = images[modalIndex] || null;
    const canPrev = images.length > 1;
    const canNext = images.length > 1;
    const maxThumbs = 6;
    const thumbStart = images.length <= maxThumbs
        ? 0
        : Math.max(0, Math.min(modalIndex - 1, images.length - maxThumbs));
    const thumbEnd = Math.min(thumbStart + maxThumbs, images.length);

    const modal = modalOpen && currentImage ? (
        <div className="product-images-modal" onClick={closeModal} role="dialog" aria-modal="true" aria-label="Product image gallery">
            <div className="product-images-modal__inner" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="product-images-modal__close" onClick={closeModal} aria-label="Close">
                    ×
                </button>
                <div className="product-images-modal__main">
                    <img src={currentImage} alt="" draggable={false} />
                </div>
                {images.length > 1 && (
                    <>
                        <div className="product-images-modal__nav">
                            <button type="button" className="product-images-modal__prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} disabled={!canPrev} aria-label="Previous image" />
                            <button type="button" className="product-images-modal__next" onClick={(e) => { e.stopPropagation(); goNext(); }} disabled={!canNext} aria-label="Next image" />
                        </div>
                        <div className="product-images-modal__counter">
                            {modalIndex + 1} / {images.length}
                        </div>
                        <div className="product-images-modal__thumbs">
                            {Array.from({ length: thumbEnd - thumbStart }, (_, i) => thumbStart + i).map((i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`product-images-modal__thumb ${i === modalIndex ? 'is-active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setModalIndex(i); }}
                                    aria-label={`Image ${i + 1}`}
                                    aria-current={i === modalIndex}
                                >
                                    <img src={images[i]} alt="" />
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    ) : null;

    return (
        <div className="product-images-web">
            <div className="main-image" onClick={() => openModal(images.indexOf(mainImage))} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && openModal(images.indexOf(mainImage))} aria-label="View full size">
                <img src={mainImage} alt="" />
            </div>

            <div className="small-images">
                {images.map((imgSrc, key) => (
                    <div
                        key={key}
                        onClick={() => { changeImage(key); openModal(key); }}
                        className={mainImage === imgSrc ? 'small-images-item active' : 'small-images-item'}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && (changeImage(key), openModal(key))}
                        aria-label={`View image ${key + 1}`}
                    >
                        <img src={imgSrc} alt="" />
                    </div>
                ))}
            </div>

            {modal && createPortal(modal, document.body)}
        </div>
    );
}
